import { t } from 'i18n';
import React, { useState, useMemo, useEffect } from 'react';
import {
  styled,
  Row,
  Checkbox,
  BaseProps,
  CommonStyles,
  Body,
  Col,
  useModal,
  ThemeProps,
} from '@teko/ds-desktop';
import { debounce, toFixed } from '@teko/utils';
import { CartItem } from '@teko/cart/v2';
import { ProductInfo } from './ProductInfo';
import { ProductPromotion } from './ProductPromotion';
import { ProductNoteInput, ProductPrice } from '../shared';
import { getDisabledUserActions } from '../utils';
import { Quantity } from 'components/Quantity';
import { getEnv } from 'helpers';
import TierPromotions from 'components/TierPromotions';
import { useTenantConfig } from 'hooks';
import { useCartV2 } from 'contexts';
import { ITEM_UPDATE_DEBOUNCE_TIME } from '../utils/constants';

export interface CartLineProps extends BaseProps {
  item: CartItem;
  onCheck?: () => void;
  checked?: boolean;
  onChangeQuantity?: (value: number) => Promise<void>;
  showCheck?: boolean;
  onRemove?: () => void;
  onEditPrice?: () => void;
  error?: string;
  isMobile?: boolean;
  isAllowEditPrice?: boolean;
  isQuantityDecimal?: boolean;
  isViewOnly?: boolean;
  simpleQuantity?: boolean;
  showDeleteButton?: boolean;
  comboPromotion?: number;
  showTierPromotions?: boolean;
  isApplicableWithCombo?: boolean;
}

const QuantitySelect = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const StyledCard = styled.div`
  ${CommonStyles};
  padding: 1rem;
  background-color: white;
`;
const StyledButtonDelete = styled(Body)(
  ({ theme }: ThemeProps) => `
  margin-top: 12px;
  cursor: pointer;
  color: ${theme?.colors.link500};

  :hover {
    color: ${theme?.colors.error500};
  }

  &.is-disabled {
    opacity: 0.5;
    cursor: default;
    color: ${theme?.colors.textSecondary};
  }
`,
);

const StyledTotalPrice = styled.span`
  width: max-content;
  display: flex;
  justify-content: flex-end;
  font-size: 15px;
  font-weight: bold;
`;

const Error = styled(Body)`
  margin-top: 2px;
  position: relative;
`;

const StyledSimpleQuantityWrapper = styled(Body)`
  text-align: center;
  width: 100%;
`;

const PolicyList = ({ policyList }: { policyList: string[] }) => (
  <Row justify="start" wrap={false}>
    <Col flex="4%" />
    <Col flex="96%">
      {policyList.map((policy, index) => (
        <Body level={2} weight="regular" key={index} color="error500">
          {policy}
        </Body>
      ))}
    </Col>
  </Row>
);

export const CartLine: ({
  item,
  onChangeQuantity,
  onRemove,
  onCheck,
  showCheck,
  error,
  onEditPrice,
  isMobile,
  isAllowEditPrice,
  isQuantityDecimal,
  isViewOnly,
  simpleQuantity,
  showDeleteButton,
  comboPromotion,
  showTierPromotions,
  isApplicableWithCombo,
  ...props
}: CartLineProps) => JSX.Element = ({
  item,
  onChangeQuantity,
  onRemove,
  onCheck,
  showCheck = false,
  error,
  onEditPrice,
  isAllowEditPrice = false,
  isQuantityDecimal = false,
  isViewOnly = false,
  simpleQuantity = false,
  showDeleteButton = true,
  comboPromotion,
  showTierPromotions = false,
  isApplicableWithCombo,
  ...props
}: CartLineProps) => {
  const { epicOmni1553, epicOmni1585 } = useTenantConfig();
  const { isUpdatingItemQuantity } = useCartV2();

  // number shown in Quantity Component
  const [productQuantity, setProductQuantity] = useState(item.quantity || 0);

  const precision = isQuantityDecimal ? 3 : 0;

  // set productQuantity when item.quantity changed
  useEffect(() => {
    setProductQuantity(item.quantity);
  }, [item.quantity]);

  const { showModal } = useModal();

  // check if these below actions is disabled, based on allowedUserAction field of each Item
  const {
    disableCheckItem,
    disableChangeItemQuantity,
    disableDeleteItem,
    disableEditItemPrice,
  } = getDisabledUserActions(item);

  const cartLineError = error;

  const handleRemoveFromCart = () => {
    showModal?.({
      title: t('Chú ý'),
      children: (
        <Body level={1} style={{ textAlign: 'center' }}>
          {t('Bạn muốn xoá sản phẩm này ra khỏi giỏ hàng?')}
        </Body>
      ),
      onOk: onRemove,
      width: 400,
      okText: t('Đồng ý'),
      cancelText: t('Hủy bỏ'),
      alignFooter: 'center',
      centeredTitle: true,
    });
  };

  // delay action, API call until user done input number
  const debounceChange = useMemo(() => {
    const debounceUpdate = debounce(
      async (quantity: number, onError?: (error: unknown) => void) => {
        try {
          await onChangeQuantity?.(toFixed({ value: quantity, precision }));
        } catch (e) {
          onError?.(e);
        }
      },
      ITEM_UPDATE_DEBOUNCE_TIME,
    );
    return (newQuantity: number) => {
      setProductQuantity(newQuantity);
      debounceUpdate(newQuantity, () => setProductQuantity(item.quantity || 0));
    };
  }, [item.quantity, onChangeQuantity, precision]);

  return (
    <StyledCard {...props}>
      <Row justify="space-between" align="middle">
        {!isViewOnly && (
          <Col flex="4%">
            {showCheck && (
              <Checkbox
                checked={item.isSelected}
                onChange={onCheck}
                disabled={disableCheckItem}
              />
            )}
          </Col>
        )}
        <Col flex={!isViewOnly ? '96%' : '100%'}>
          <Row justify="space-between">
            <Col span={6}>
              <ProductInfo product={item} />
            </Col>
            <Col span={2} paddingRight="s16">
              {/*Only show price if this item is existed*/}
              {item.sku && (
                <ProductPrice
                  price={item.price}
                  strikeThroughDisplayPrice={item.strikeThroughDisplayPrice}
                  onEditPrice={onEditPrice}
                  editable={isAllowEditPrice}
                  disableEditItemPrice={disableEditItemPrice}
                  isDesktop
                />
              )}
            </Col>
            <Col span={2}>
              {!simpleQuantity ? (
                <QuantitySelect>
                  <Quantity
                    quantity={productQuantity}
                    setQuantity={(value) => {
                      if (value !== productQuantity) {
                        debounceChange(value);
                      }
                    }}
                    onPlus={() => {
                      debounceChange(productQuantity + 1);
                    }}
                    onMinus={() => {
                      if (productQuantity - 1 > 0) {
                        debounceChange(productQuantity - 1);
                      }
                    }}
                    min={Math.pow(10, -precision)}
                    {...(getEnv('EPIC_ECOM_489') !== 'enabled'
                      ? { decimalSeparator: '.' }
                      : {})}
                    precision={precision}
                    blurOnEnter
                    disabled={
                      disableChangeItemQuantity ||
                      isUpdatingItemQuantity[item.id]
                    }
                  />
                  {showDeleteButton && (
                    <StyledButtonDelete
                      level={1}
                      className={disableDeleteItem ? 'is-disabled' : ''}
                      {...(disableDeleteItem
                        ? {}
                        : { onClick: handleRemoveFromCart })}
                    >
                      {t('Xóa')}
                    </StyledButtonDelete>
                  )}
                  {cartLineError && (
                    <Error level={1} color="error500" weight="regular" line={2}>
                      {cartLineError}
                    </Error>
                  )}
                </QuantitySelect>
              ) : (
                <StyledSimpleQuantityWrapper color="textSecondary">
                  {productQuantity}
                </StyledSimpleQuantityWrapper>
              )}
            </Col>
            <Col span={2}>
              {/*Only show price if this item is existed, otherwise show its displayPrice*/}
              {item.sku ? (
                <ProductPrice price={item.rowTotal} isDesktop />
              ) : (
                <StyledTotalPrice>{item.displayPrice}</StyledTotalPrice>
              )}
            </Col>
          </Row>
          {!!item.promotions?.length && (
            <Row>
              <Col span={1.5} />
              <Col span={10.5}>
                {!!showTierPromotions && (
                  <TierPromotions
                    productPromotions={item.promotions}
                    isApplicableWithCombo={isApplicableWithCombo}
                  />
                )}
                {epicOmni1585 && (
                  <div style={{ marginTop: '16px' }}>
                    <TierPromotions
                      productPromotions={item.promotions}
                      isApplicableWithCombo={isApplicableWithCombo}
                      isCustomerTierPromotions
                    />
                  </div>
                )}
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      {!!item.promotions ? (
        <ProductPromotion
          productPromotions={item.promotions}
          productId={item.id}
          isViewOnly={isViewOnly}
          productSku={item.sku}
          comboPromotion={comboPromotion}
        />
      ) : null}
      {!!item.policies.length ? (
        <PolicyList policyList={item.policies} />
      ) : null}
      {!!epicOmni1553 && !comboPromotion && (
        <Row>
          {!isViewOnly && <Col flex="4%"> </Col>}
          <Col flex={isViewOnly ? '100%' : '96%'}>
            <ProductNoteInput item={item} />
          </Col>
        </Row>
      )}
    </StyledCard>
  );
};
